import { useTranslation } from 'react-i18next';
import type { RouteLine } from '../../../types/climb';
import { PhotoLineCanvas } from '../../../components/climbs/photo-editor/PhotoLineCanvas';
import { PhotoLineControls } from '../../../components/climbs/photo-editor/PhotoLineControls';
import { usePhotoLine } from '../../../components/climbs/photo-editor/usePhotoLine';
import { ScanEditorLayout, type ScanEditorChrome } from './ScanEditorLayout';

interface ScanLineEditorProps {
  imageUrl: string;
  naturalWidth: number;
  naturalHeight: number;
  line: RouteLine | null;
  onChange: (line: RouteLine | null) => void;
  /**
   * Offer the corridor — the band of wall left lit around the line. On a gym
   * wall it answers "which of these holds are mine"; on rock there are no holds
   * to tell apart and the line is the whole topo, so the crag editor turns it
   * off and the lines it saves carry `corridor: 0`, which every renderer reads
   * as "leave the photograph alone".
   */
  allowCorridor?: boolean;
  /**
   * Offer the line's thickness. Off where it belongs to the picture rather than
   * to the route — a crag's sector owns it, so a dozen lines on one cliff read
   * as one topo instead of as a dozen people's handwriting.
   */
  allowThickness?: boolean;
  /** The page's half of the two columns these controls live in. */
  chrome: ScanEditorChrome;
}

/**
 * Draws the line of a route on its scan photo, in the two columns of the scans
 * page — and, with the corridor and thickness turned off, on a sector's photo in
 * the crag panel.
 *
 * The editor itself is shared with the member app's climb editor (see
 * `components/climbs/photo-editor`); this is the panel's layout and its copy.
 */
export function ScanLineEditor({
  imageUrl,
  naturalWidth,
  naturalHeight,
  line,
  onChange,
  allowCorridor = true,
  allowThickness = true,
  chrome,
}: ScanLineEditorProps) {
  const { t } = useTranslation();
  const editor = usePhotoLine({ naturalWidth, naturalHeight, line, onChange, allowCorridor });

  const canvas = (
    <PhotoLineCanvas
      editor={editor}
      imageUrl={imageUrl}
      testId="scan-line-frame"
      className="rounded-md border"
      resetZoomLabel={t('gymAdmin.scans.resetZoom')}
    />
  );

  return (
    <ScanEditorLayout canvas={canvas} {...chrome}>
      <PhotoLineControls
        editor={editor}
        allowCorridor={allowCorridor}
        allowThickness={allowThickness}
        labels={{
          points: (count) => t('gymAdmin.scans.linePoints', { count }),
          thickness: t('gymAdmin.scans.thickness'),
          corridor: t('gymAdmin.scans.corridor'),
          corridorHint: t('gymAdmin.scans.corridorHint'),
          deletePoint: t('gymAdmin.scans.deletePoint'),
          undoPoint: t('gymAdmin.scans.undoPoint'),
          clearLine: t('gymAdmin.scans.clearLine'),
          hint: t('gymAdmin.scans.lineHint'),
        }}
      />
    </ScanEditorLayout>
  );
}
